import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MenuLateralInfoComponent } from './menu-lateral-info/menu-lateral-info.component';
import { RegistroComponent } from './registro/registro.component';
import { AnamnesisComponent } from './anamnesis/anamnesis.component';
import { ExamenesComponent } from './examenes/examenes.component';
import { DiagnosticoComponent } from './diagnostico/diagnostico.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { HeaderSophiaComponent } from '../../common/components/header-sophia/header-sophia.component';
import { Router, RouterLink } from '@angular/router';
import { TratamientoComponent } from './tratamiento/tratamiento.component';
import { FichaClinicaStore } from '../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { FichaClinicaService } from '../../services/ficha-clinica.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalErrorGeneralComponent } from '../../common/modal-error-general/modal-error-general.component';
import { ShareDataService } from '../../../shared/services/share-data.service';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { IDataPacienteForm } from '../../../shared/interfaces/paciente-form.interface';
import { CommonModule } from '@angular/common';
import { UtilServiceService } from '../../../shared/services/util-service.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { AppPermissions, ValidRoles } from '../../../shared/enums';
import { HasAccessDirective } from '../../../shared/directives/has-access/has-access.directive';
import { AuthenticationService } from '../../services/authentication.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { SophiaAgentAIDiagnosisModalComponent } from './sophia-agent-ai/diagnosis-ai-modal/diagnosis-ai-modal.component';
import { fichaClinicaFinalizada } from '../../../shared/helpers/ficha-clinica.helper';

export enum TabPosition {
  Identificacion,
  Anamnesis,
  Diagnostico,
  Tratamiento,
  Examenes,
}

export enum ScrollButton {
  BUTTON_UP = 'UP',
  BUTTON_DOWN = 'DOWN',
}

export enum TabComponents {
  IDENTIFICACION = 'identification',
  ANAMNESIS = 'anamnesis',
  DIAGNOSIS = 'diagnostico',
  TREATMENT = 'tratamiento',
  EXAMS = 'examenes',
}

@Component({
  selector: 'soph-ficha-clinica',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    HeaderSophiaComponent,
    RegistroComponent,
    AnamnesisComponent,
    DiagnosticoComponent,
    TratamientoComponent,
    ExamenesComponent,
    HasAccessDirective,
    RouterLink,
    MenuLateralInfoComponent,
    MatTooltip,
    MatRippleModule,
  ],
  templateUrl: './ficha-clinica.component.html',
  styleUrl: './ficha-clinica.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('buttonAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(-10px)' })),
      ]),
    ]),
  ],
})
export class FichaClinicaComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly dialog: MatDialog = inject(MatDialog);
  private readonly auth: AuthenticationService = inject(AuthenticationService);
  private readonly medicOnlyTabs: Set<TabComponents> = new Set<TabComponents>([
    TabComponents.DIAGNOSIS,
    TabComponents.EXAMS,
    TabComponents.TREATMENT,
  ]);

  dataNuevaConsulta = new BehaviorSubject<any | null>(null);
  horaNuevaConsulta!: string;
  fechaNuevaConsulta!: string;
  tipoAtencion!: string;
  public presencial = 'Presencial';
  public domiciliaria = 'Domiciliaria';
  public telemedicina = 'Telemedicina';

  existePaciente = new BehaviorSubject<any | null>(null);
  nombrePaciente!: string;
  apellidosPaciente!: string;
  historiaClinica!: string;
  dniPaciente!: string;
  correoPaciente!: string;
  telefonoPaciente!: string;
  previsionPaciente!: string;
  tipoDocumentoPaciente!: string;
  valueTipoDocumentoPaciente!: string;

  showTopButton: boolean = false;
  showBottomButton: boolean = true;
  @ViewChild('scrollableDiv') scrollableDiv!: ElementRef;
  @ViewChild('headerHeight') headerHeight!: ElementRef;
  @ViewChild('navHeight') navHeight!: ElementRef;
  @ViewChild('infoHeight') infoHeight!: ElementRef;
  @ViewChild('seguimientoDiv') seguimientoDiv!: ElementRef;
  @ViewChild('floatingTabBar') floatingTabBar!: ElementRef;
  mostrarTabsFijos = false;
  downBtnPosition: string = '20px';
  downBtnInitialValue: number = 0;
  upBtnPosition: string = '20px';
  upBtnLastValue: number = 30;
  scrollButton = ScrollButton;
  isGoDownButtonHovered = false;
  isGoUpButtonHovered = false;

  protected readonly selectedTabIndex = signal<number>(0);

  readonly tabs = [
    {
      label: 'Identificación',
      component: TabComponents.IDENTIFICACION,
      permissions: [AppPermissions.SEE_IDENTIFICATION],
      disabled: this.disableTab(TabComponents.IDENTIFICACION),
    },
    {
      label: 'Anamnesis',
      component: TabComponents.ANAMNESIS,
      permissions: [AppPermissions.SEE_ANAMNESIS],
      disabled: this.disableTab(TabComponents.ANAMNESIS),
      tab: RegistroComponent,
    },
    {
      label: 'Diagnóstico',
      component: TabComponents.DIAGNOSIS,
      permissions: [AppPermissions.SEE_DIAGNOSIS],
      disabled: this.disableTab(TabComponents.DIAGNOSIS),
    },
    {
      label: 'Tratamiento',
      component: TabComponents.TREATMENT,
      permissions: [AppPermissions.SEE_TREATMENT],
      disabled: this.disableTab(TabComponents.TREATMENT),
    },
    {
      label: 'Exámenes',
      component: TabComponents.EXAMS,
      permissions: [AppPermissions.SEE_EXAMS],
      disabled: this.disableTab(TabComponents.EXAMS),
    },
  ];

  protected readonly TabPosition = TabPosition;
  protected readonly AppPermissions = AppPermissions;
  protected readonly rol: WritableSignal<string | null> = signal(null);

  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly icons = ['check', 'alert'];

  protected enableAI: boolean = false;

  constructor(
    private readonly _router: Router,
    private readonly _fichaClinicaStore: FichaClinicaStore,
    private readonly _fichaClinicaService: FichaClinicaService,
    private readonly _shareDataService: ShareDataService,
    private readonly _utilService: UtilServiceService
  ) {
    this.icons.forEach((icon) => {
      this.iconRegistry.addSvgIcon(
        icon,
        this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons-v2/${icon}.svg`)
      );
    });

    this._fichaClinicaStore.state$.subscribe((state) => {
      const hasHistorial =
        state.historial.identificacion && state.historial.anamnesis && state.hasPreviewAppointment;

      let allFilled = false;

      const anamnesisData = this._shareDataService.getAnamnesisData().value;
      if (anamnesisData) {
        const obj = anamnesisData as any;
        const props = [
          'temperatura',
          'frecuenciaCardiaca',
          'presionArterial',
          'saturacion',
          'relato',
        ];
        allFilled = props.every(
          (prop) => obj[prop] !== null && obj[prop] !== undefined && obj[prop] !== ''
        );
      }

      this.enableAI = hasHistorial && allFilled && !fichaClinicaFinalizada(state.estado || '');
    });

    if (!this._fichaClinicaStore.values.idAgendamiento) {
      this.irAregistro();
    } else {
      this.getFichaClinica();
    }

    this.rol.set(this.getRoleLabel(this.auth.userRole.value) || null);

    this._fichaClinicaStore.state$.pipe(distinctUntilChanged()).subscribe((values) => {
      this.selectedTabIndex.set(values.tabActualSeleccionado);
    });
  }

  ngOnInit(): void {
    this.getDataNuevaConsulta();
    this.getDataPaciente();
  }

  ngAfterViewInit(): void {
    const headerHeight: number = this.headerHeight.nativeElement.offsetHeight;
    const navHeight: number = this.navHeight.nativeElement.offsetHeight;
    const infoHeight: number = this.infoHeight.nativeElement.offsetHeight;
    const viewHeight: number = this.scrollableDiv.nativeElement.offsetHeight;
    this.downBtnInitialValue =
      headerHeight + navHeight + infoHeight + Math.trunc(viewHeight * 0.0515);
    this.downBtnPosition = `${this.downBtnInitialValue}px`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        this.mostrarTabsFijos = !entry.isIntersecting;
      },
      {
        threshold: 0,
      }
    );

    observer.observe(this.seguimientoDiv.nativeElement);
  }

  ngOnDestroy(): void {
    this._fichaClinicaStore.resetStore();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    const downBtnPositionValue: number =
      window.scrollY < this.downBtnInitialValue
        ? this.downBtnInitialValue - window.scrollY
        : 20 + (this.floatingTabBar?.nativeElement.offsetHeight || 0);
    const pxToBottom: number =
      this.scrollableDiv.nativeElement.offsetHeight -
      (window.scrollY + window.visualViewport!.height);
    const upBtnPositionValue: number = pxToBottom < 20 ? this.upBtnLastValue - pxToBottom : 20;
    this.downBtnPosition = `${downBtnPositionValue}px`;
    this.upBtnPosition = `${upBtnPositionValue}px`;
    this.showTopButton = window.scrollY > 350;
    this.showBottomButton =
      window.document.body.scrollHeight - window.innerHeight - window.scrollY > 350;
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.setHoversToFalse();
  }

  scrollToBottom() {
    window.scrollTo({
      top: window.document.body.scrollHeight - window.innerHeight,
      behavior: 'smooth',
    });
    this.setHoversToFalse();
  }

  setHoversToFalse(): void {
    this.isGoUpButtonHovered = false;
    this.isGoUpButtonHovered = false;
  }

  mouseEvent({ type }: MouseEvent, origen: ScrollButton) {
    if (origen === this.scrollButton.BUTTON_UP) this.isGoUpButtonHovered = type === 'mouseenter';
    if (origen === this.scrollButton.BUTTON_DOWN)
      this.isGoDownButtonHovered = type === 'mouseenter';
  }

  private getDataNuevaConsulta() {
    this.horaNuevaConsulta = this._fichaClinicaStore.values.horaAtencion;
    this.fechaNuevaConsulta = this._fichaClinicaStore.values.fechaAtencion;
    this.tipoAtencion = this._utilService.formatUpperCaseFirstLetter(
      this._fichaClinicaStore.values.tipoAtencion
    );
  }

  private getFichaClinica() {
    this._fichaClinicaService.info(this._fichaClinicaStore.values.idAgendamiento!).subscribe({
      next: (response) => {
        if (response.data) {
          this._fichaClinicaStore.updateStore({
            ...this._fichaClinicaStore.values,
            estado: response.data.estado,
          });
        }
      },
      error: (error) => {
        this.dialog.open(ModalErrorGeneralComponent, {
          width: '452px',
          disableClose: true,
          data: {
            message: error.error.error.message,
            callbackUrl: '/calendario',
          },
        });
      },
    });
  }

  private getDataPaciente() {
    this._shareDataService.getDataExistePaciente().subscribe({
      next: (data) => {
        if (data) {
          this.nombrePaciente = data.nombres;
          this.apellidosPaciente = data.apellidos;
          this.previsionPaciente = data.prevision.nombre;
          this.correoPaciente = data.correoElectronico;
          this.telefonoPaciente = data.telefono;
          this.tipoDocumentoPaciente = data.tipoDocumento?.nombre;
          this.valueTipoDocumentoPaciente = data.numeroDocumento;
        }
      },
    });
  }

  public irAregistro() {
    const vaciarAgendamiento: IDataPacienteForm = {
      apellidos: '',
      casaDepto: '',
      direccion: '',
      direccionCompleta: '',
      email: '',
      fechaSeleccionada: '',
      horaSeleccionada: '',
      institucion: '',
      link: '',
      nombre: '',
      numero: '',
      numeroDocumento: '',
      numeroDocumentoOtros: '',
      numeroPasaporte: '',
      prevision: '',
      tipoAtencion: '',
      tipoDocumento: '',
    };
    this._shareDataService.setFormData(vaciarAgendamiento);
    this._router.navigate(['/ingreso'], { replaceUrl: true });
  }

  public onSelectedTabChange(event: MatTabChangeEvent) {
    const { index } = event;

    this._fichaClinicaStore.updateStore({
      ...this._fichaClinicaStore.values,
      tabActualSeleccionado: index,
    });
  }

  protected handleSetAnamnesis(shouldSet: boolean) {
    if (shouldSet) {
      this.selectedTabIndex.set(TabPosition.Anamnesis);
    }
  }

  isMedic() {
    return this.auth.hasRole(ValidRoles.MEDIC);
  }

  disableTab(tab: TabComponents): boolean {
    const result = this.medicOnlyTabs.has(tab) && !this.isMedic();
    return result;
  }

  private getRoleLabel(rol: string): string {
    switch (rol) {
      case 'MEDICO':
        return 'Médico';
      case 'ASISTENTE':
        return 'Asistente';
      case 'ASISTENTE_RECEPCION':
        return 'Asistente de Recepción';
      default:
        return rol;
    }
  }

  openSophiaAgentAI() {
    this.dialog.open(SophiaAgentAIDiagnosisModalComponent, { disableClose: true });
  }
}
